import { ProgressState } from '../state/ProgressState';
import { LevelLoader } from './LevelLoader';
import type { ResultsPayload } from '../../shared/types';

// ============================================
// Step 12: Progression System
// ============================================

export interface ScoreBreakdown {
  wavesCompleted: number;
  wavePoints: number;
  villagersRemaining: number;
  villagerPoints: number;
  monsterDamageDealt: number;  // Total HP of killed monsters
  damagePoints: number;
  coinsRemaining: number;
  coinPoints: number;
  gemsRemaining: number;
  gemPoints: number;
  towersBuilt: number;
  towerPenalty: number;
  waveSkipBonuses: number;
  skipBonusPoints: number;
  victoryBonus: number;
  totalScore: number;
}

export interface StarRating {
  stars: number;          // 0-3
  villagerPercent: number;
  thresholds: {
    oneStar: number;
    twoStar: number;
    threeStar: number;
  };
}

// Score multipliers
const SCORE_CONFIG = {
  WAVE_POINTS: 100,           // Points per wave completed
  VILLAGER_POINTS: 50,        // Points per villager remaining (highest weight)
  GEM_POINTS: 5,              // Points per gem remaining
  COIN_POINTS: 1,             // Points per coin remaining (lowest weight)
  DAMAGE_POINTS: 1,           // Points per monster HP dealt (killed monsters only)
  TOWER_PENALTY: 50,          // Points deducted per tower (encourages efficiency)
  SKIP_BONUS: 100,            // Bonus per valid wave skip (>5s early)
  VICTORY_BONUS: 1000,        // Bonus for winning
  DEFEAT_PENALTY: -500,       // Penalty for losing

  // Star thresholds (villager percentage)
  THREE_STAR_THRESHOLD: 0.8,  // 80%+ villagers = 3 stars
  TWO_STAR_THRESHOLD: 0.5,    // 50%+ villagers = 2 stars
  ONE_STAR_THRESHOLD: 0.0,    // Any completion = 1 star
};

class ProgressionSystemClass {
  // ============================================
  // Score Calculation
  // ============================================

  /**
   * Calculate detailed score breakdown from results
   */
  calculateScore(results: ResultsPayload): ScoreBreakdown {
    const wavePoints = results.wavesCompleted * SCORE_CONFIG.WAVE_POINTS;
    const villagerPoints = results.villagersRemaining * SCORE_CONFIG.VILLAGER_POINTS;
    const gemPoints = (results.gemsRemaining || 0) * SCORE_CONFIG.GEM_POINTS;
    const coinPoints = (results.coinsRemaining || 0) * SCORE_CONFIG.COIN_POINTS;
    const damagePoints = (results.monsterDamageDealt || 0) * SCORE_CONFIG.DAMAGE_POINTS;
    const towerPenalty = results.towersBuilt * SCORE_CONFIG.TOWER_PENALTY;
    const skipBonusPoints = (results.waveSkipBonuses || 0) * SCORE_CONFIG.SKIP_BONUS;
    const victoryBonus = results.victory ? SCORE_CONFIG.VICTORY_BONUS : SCORE_CONFIG.DEFEAT_PENALTY;

    const totalScore = Math.max(0,
      wavePoints +
      villagerPoints +
      gemPoints +
      coinPoints +
      damagePoints -
      towerPenalty +
      skipBonusPoints +
      victoryBonus
    );

    return {
      wavesCompleted: results.wavesCompleted,
      wavePoints,
      villagersRemaining: results.villagersRemaining,
      villagerPoints,
      monsterDamageDealt: results.monsterDamageDealt || 0,
      damagePoints,
      coinsRemaining: results.coinsRemaining || 0,
      coinPoints,
      gemsRemaining: results.gemsRemaining || 0,
      gemPoints,
      towersBuilt: results.towersBuilt,
      towerPenalty,
      waveSkipBonuses: results.waveSkipBonuses || 0,
      skipBonusPoints,
      victoryBonus,
      totalScore,
    };
  }

  /**
   * Calculate star rating based on villager survival
   */
  calculateStars(villagersRemaining: number, maxVillagers: number, victory: boolean): StarRating {
    const percent = maxVillagers > 0 ? villagersRemaining / maxVillagers : 0;

    let stars = 0;
    if (victory) {
      if (percent >= SCORE_CONFIG.THREE_STAR_THRESHOLD) {
        stars = 3;
      } else if (percent >= SCORE_CONFIG.TWO_STAR_THRESHOLD) {
        stars = 2;
      } else {
        stars = 1;
      }
    }

    return {
      stars,
      villagerPercent: percent,
      thresholds: {
        oneStar: SCORE_CONFIG.ONE_STAR_THRESHOLD,
        twoStar: SCORE_CONFIG.TWO_STAR_THRESHOLD,
        threeStar: SCORE_CONFIG.THREE_STAR_THRESHOLD,
      },
    };
  }

  // ============================================
  // Progress Updates
  // ============================================

  /**
   * Process completion of a scenario and update progress
   */
  processCompletion(results: ResultsPayload): {
    scoreBreakdown: ScoreBreakdown;
    starRating: StarRating;
    newUnlocks: string[];
    isNewHighScore: boolean;
    isNewBestTime: boolean;
  } {
    const scoreBreakdown = this.calculateScore(results);

    // Get max villagers from scenario
    const scenario = LevelLoader.getScenarioDef(results.scenarioId);
    const maxVillagers = scenario?.startingVillagers ?? 20;

    const starRating = this.calculateStars(
      results.villagersRemaining,
      maxVillagers,
      results.victory
    );

    // Get current progress for comparison
    const currentProgress = ProgressState.getScenarioProgress(results.scenarioId);
    const isNewHighScore = scoreBreakdown.totalScore > currentProgress.bestScore;
    const isNewBestTime = currentProgress.bestTime === 0 ||
      (results.victory && results.timeElapsed < currentProgress.bestTime);

    // Update progress state
    const newUnlocks: string[] = [];

    if (results.victory) {
      // Record victory with score, time, and player count (for high-score display)
      ProgressState.recordVictory(
        results.scenarioId,
        scoreBreakdown.totalScore,
        results.timeElapsed,
        results.playerCount ?? 1
      );

      // Unlock next levels
      newUnlocks.push(...this.unlockConnectedScenarios(results.scenarioId));
    }

    return {
      scoreBreakdown,
      starRating,
      newUnlocks,
      isNewHighScore,
      isNewBestTime,
    };
  }

  /**
   * Unlock scenarios connected to the completed one
   */
  private unlockConnectedScenarios(completedScenarioId: string): string[] {
    const unlocked: string[] = [];

    // Get the scenario definition to find what it unlocks
    const scenario = LevelLoader.getScenarioDef(completedScenarioId);
    if (scenario && scenario.unlocks) {
      for (const nextId of scenario.unlocks) {
        // Check if next level exists and isn't already unlocked
        const nextScenario = LevelLoader.getScenarioDef(nextId);
        if (nextScenario && !ProgressState.isScenarioUnlocked(nextId)) {
          ProgressState.unlockScenario(nextId);
          unlocked.push(nextId);
          console.log(`[ProgressionSystem] Unlocked: ${nextId}`);
        }
      }
    }

    return unlocked;
  }

  // ============================================
  // Progress Queries
  // ============================================

  /**
   * Get completion status for all scenarios
   */
  getAllScenarioStatus(): Array<{
    id: string;
    name: string;
    unlocked: boolean;
    completed: boolean;
    bestScore: number;
    bestTime: number;
    bestPlayerCount: number;
    stars: number;
  }> {
    const scenarios = LevelLoader.getAvailableScenarios();

    return scenarios.map(id => {
      const def = LevelLoader.getScenarioDef(id);
      const progress = ProgressState.getScenarioProgress(id);

      // Calculate stars from best score (approximate)
      let stars = 0;
      if (progress.completed) {
        // Use stored best score to estimate stars
        // This is a simplification - ideally we'd store stars directly
        const maxVillagers = def?.startingVillagers ?? 20;
        const estimatedVillagers = Math.round((progress.bestScore - 1000) / 50); // Reverse of scoring
        const percent = Math.min(1, Math.max(0, estimatedVillagers / maxVillagers));

        if (percent >= SCORE_CONFIG.THREE_STAR_THRESHOLD) stars = 3;
        else if (percent >= SCORE_CONFIG.TWO_STAR_THRESHOLD) stars = 2;
        else stars = 1;
      }

      return {
        id,
        name: def?.name ?? id,
        unlocked: progress.unlocked,
        completed: progress.completed,
        bestScore: progress.bestScore,
        bestTime: progress.bestTime,
        bestPlayerCount: progress.bestPlayerCount ?? 1,
        stars,
      };
    });
  }

  /**
   * Get total stars earned across all scenarios
   */
  getTotalStars(): { earned: number; possible: number } {
    const statuses = this.getAllScenarioStatus();
    const earned = statuses.reduce((sum, s) => sum + s.stars, 0);
    const possible = statuses.length * 3;
    return { earned, possible };
  }

  /**
   * Get overall completion percentage
   */
  getOverallCompletion(): number {
    const statuses = this.getAllScenarioStatus();
    const completed = statuses.filter(s => s.completed).length;
    return statuses.length > 0 ? completed / statuses.length : 0;
  }

  /**
   * Check if all scenarios are completed
   */
  isGameComplete(): boolean {
    const statuses = this.getAllScenarioStatus();
    return statuses.every(s => s.completed);
  }

  /**
   * Get the next recommended scenario to play
   */
  getNextRecommendedScenario(): string | null {
    const statuses = this.getAllScenarioStatus();

    // Find first unlocked but not completed
    for (const status of statuses) {
      if (status.unlocked && !status.completed) {
        return status.id;
      }
    }

    // All completed or none unlocked, return first unlocked
    const firstUnlocked = statuses.find(s => s.unlocked);
    return firstUnlocked?.id ?? null;
  }

  // ============================================
  // Reset
  // ============================================

  /**
   * Reset all progress (for debugging or new game+)
   */
  resetAllProgress(): void {
    ProgressState.reset();
    console.log('[ProgressionSystem] All progress reset');
  }
}

// Singleton instance
export const ProgressionSystem = new ProgressionSystemClass();
